import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CustomTooltip from './CustomTooltip';
import { ChannelControl, ChannelControlStyle, getChannelIcon } from './ChannelControl';
import { DefaultTag } from './DefaultTag';
import { BORDER, CATEGORY_COLUMN_WIDTH, INK, MIN_CHANNEL_COLUMN_WIDTH } from './notificationTokens';
import {
  NotificationCategoryDefinitionType,
  NotificationChannelDefinitionType,
  NotificationRoleDefinitionType,
  NotificationTypeChannels,
  effectiveOn,
  isOwnerLocked,
} from '../../util/NotificationUtil';

interface NotificationMatrixProps {
  roles: NotificationRoleDefinitionType[];
  channels: NotificationChannelDefinitionType[];
  categories: NotificationCategoryDefinitionType[];
  settings: NotificationTypeChannels;
  controlStyle: ChannelControlStyle;
  onToggle: (channel: string, category: string, role: string, value: boolean) => void;
}

const NotificationMatrix = ({
  roles,
  channels,
  categories,
  settings,
  controlStyle,
  onToggle,
}: NotificationMatrixProps) => {
  const multiChannel = channels.length > 1;
  const ownerValue = roles[0]?.value;
  // Matches the panel's matrix→cards switch threshold, so whenever the matrix is
  // shown it fits the container exactly (no horizontal scroll).
  const minTableWidth = CATEGORY_COLUMN_WIDTH + roles.length * channels.length * MIN_CHANNEL_COLUMN_WIDTH;

  return (
    <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
      <Table
        sx={{
          minWidth: minTableWidth,
          // Fixed layout keeps every role/channel column equal width regardless
          // of header content (e.g. the "DEFAULT" tag on Account Owner), so the
          // owner column doesn't grow wider than the others.
          tableLayout: 'fixed',
          width: '100%',
          borderCollapse: 'collapse',
          '& td, & th': { borderBottom: 'none' },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              rowSpan={multiChannel ? 2 : 1}
              sx={{
                width: CATEGORY_COLUMN_WIDTH,
                whiteSpace: 'nowrap',
                verticalAlign: 'bottom',
                fontSize: 14,
                fontWeight: 600,
                color: INK.secondary,
                p: 0,
                pr: 3,
                pb: 1.5,
                borderBottom: `1px solid ${BORDER.default}`,
              }}
            >
              Notification Category
            </TableCell>
            {roles.map((role) => (
              <TableCell
                key={role.value}
                colSpan={channels.length}
                align="center"
                sx={{
                  px: 1,
                  pb: 1.5,
                  borderBottom: `1px solid ${BORDER.default}`,
                  borderLeft: `1px solid ${BORDER.subtle}`,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.75,
                    flexWrap: 'wrap',
                  }}
                >
                  <Typography component="span" sx={{ fontSize: 16, fontWeight: 600, color: INK.heading }}>
                    {role.label}
                  </Typography>
                  {role.value === ownerValue && <DefaultTag />}
                </Box>
                {role.sub && (
                  <Typography sx={{ fontSize: 12, color: INK.secondary, mt: '2px' }}>{role.sub}</Typography>
                )}
              </TableCell>
            ))}
          </TableRow>

          {multiChannel && (
            <TableRow>
              {roles.map((role) =>
                channels.map((channel) => (
                  <TableCell
                    key={`${role.value}_${channel.value}`}
                    align="center"
                    sx={{
                      py: 1.25,
                      borderBottom: `1px solid ${BORDER.default}`,
                      borderLeft: `1px solid ${BORDER.subtle}`,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: 12,
                        fontWeight: 500,
                        color: INK.secondary,
                      }}
                    >
                      {getChannelIcon(channel.value, { fontSize: 14, opacity: 0.75 })}
                      {channel.label}
                    </Box>
                  </TableCell>
                ))
              )}
            </TableRow>
          )}
        </TableHead>

        <TableBody>
          {categories.map((category, categoryIndex) => {
            const isLastRow = categoryIndex === categories.length - 1;
            const rowBorder = isLastRow ? 'none' : `1px solid ${BORDER.subtle}`;
            return (
              <TableRow key={category.value}>
                <TableCell
                  sx={{
                    width: CATEGORY_COLUMN_WIDTH,
                    p: 0,
                    pr: 3,
                    py: 2.25,
                    borderBottom: rowBorder,
                  }}
                >
                  {/* space-between lines every info icon up on the same vertical
                      line at the right edge of the category column. minWidth:0 lets
                      a long label wrap rather than spill over the next column's
                      border; the icon stays a non-shrinking item. */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
                    <Typography component="span" sx={{ fontSize: 16, fontWeight: 500, color: INK.heading, minWidth: 0 }}>
                      {category.label}
                    </Typography>
                    <Box sx={{ flexShrink: 0, display: 'inline-flex' }}>
                      <CustomTooltip title={category.tooltip} placement="top-start" />
                    </Box>
                  </Box>
                </TableCell>
                {roles.map((role) =>
                  channels.map((channel) => {
                    const row = settings[channel.value]?.[category.value];
                    const locked = role.value === ownerValue && isOwnerLocked(roles, row);
                    const on = effectiveOn(roles, row, role.value);
                    const stored = !!row?.[role.value];
                    return (
                      <TableCell
                        key={`${role.value}_${channel.value}`}
                        align="center"
                        sx={{
                          py: 2.25,
                          px: 1,
                          borderBottom: rowBorder,
                          borderLeft: `1px solid ${BORDER.subtle}`,
                        }}
                      >
                        <ChannelControl
                          channelValue={channel.value}
                          channelLabel={channel.label}
                          on={on}
                          locked={locked}
                          controlStyle={controlStyle}
                          onToggle={() => onToggle(channel.value, category.value, role.value, !stored)}
                        />
                      </TableCell>
                    );
                  })
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export { NotificationMatrix };
